import React, { useEffect, useMemo, useRef, useState } from 'react';
import { EyeOff } from 'lucide-react';
import type { PublicCardView } from '#shared';
import { soundEngine } from '../audio/soundEngine.ts';
import { CardSleeveBack, CardVisual } from './CardVisual.tsx';

interface FanHandProps {
  seatId: string;
  handCount: number;
  handCards: PublicCardView[] | null;
  sleeveUrl?: string | null;
  streamMode: boolean;
  isMyTurnMain: boolean;
  reducedMotion: boolean;
  onPlayCard: (card: PublicCardView) => void;
  onInspectCard: (card: PublicCardView) => void;
  onOpenCardMenu: (card: PublicCardView, e: React.MouseEvent) => void;
}

function hashSeed(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function unitSigned(seed: number): number {
  return ((seed % 2000) / 1000) - 1; // [-1, +1]
}

/**
 * Asymmetrical Fanned Hand:
 * - Organic per-card spacing jitter, individual tilt, and height offset
 * - Whole-hand lean bias that drops steeper on the far (right) end
 * - Subtle lean drift each time the hand composition changes
 * - Hover/tap lifts active card and pushes neighboring cards outward
 * - Stream mode renders fanned sleeves with zero hidden card data on screen
 */
export function FanHand({
  seatId,
  handCount,
  handCards,
  sleeveUrl,
  streamMode,
  isMyTurnMain,
  reducedMotion,
  onPlayCard,
  onInspectCard,
  onOpenCardMenu,
}: FanHandProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);
  const [driftStep, setDriftStep] = useState(0);

  const signature = useMemo(() => {
    if (!handCards) return `masked:${handCount}`;
    return handCards.map((c) => c.instanceId).join('|');
  }, [handCards, handCount]);

  const prevSigRef = useRef(signature);
  useEffect(() => {
    if (prevSigRef.current !== signature) {
      prevSigRef.current = signature;
      setDriftStep((s) => s + 1);
      setSelectedIdx(null);
    }
  }, [signature]);

  const activeIdx = hoveredIdx ?? selectedIdx;
  const count = handCards ? handCards.length : handCount;

  // Drift lean slightly on every hand change (like shifting grip at a real table)
  const handLeanDeg = useMemo(() => {
    if (reducedMotion) return 0;
    const h = hashSeed(`${signature}:${driftStep}`);
    return -1.6 + unitSigned(h) * 2.1; // slightly left-handed / asymmetric bias
  }, [signature, driftStep, reducedMotion]);

  if (count === 0) {
    return (
      <div
        data-zone-anchor={`${seatId}:hand`}
        className="h-24 flex items-center justify-center text-xs text-slate-500 border border-dashed border-slate-800/80 rounded-xl mx-3"
      >
        Hand empty
      </div>
    );
  }

  return (
    <div
      data-zone-anchor={`${seatId}:hand`}
      aria-label={`Player hand (${count} cards)`}
      onMouseLeave={() => setHoveredIdx(null)}
      className="relative h-[138px] sm:h-[154px] w-full flex items-end justify-center select-none pt-3 pb-1 overflow-visible"
    >
      {streamMode && !handCards && (
        <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-40 px-2.5 py-0.5 rounded-full bg-rose-950/90 border border-rose-500/60 text-[10px] text-rose-200 flex items-center gap-1 shadow">
          <EyeOff className="w-3 h-3" />
          <span>Stream Mode: Hand Hidden on Broadcast</span>
        </div>
      )}

      <div className="relative flex items-end justify-center h-full max-w-full">
        {Array.from({ length: count }).map((_, idx) => {
          const item = handCards ? handCards[idx] : null;
          const mid = (count - 1) / 2;
          const norm = count <= 1 ? 0 : (idx - mid) / Math.max(1, mid); // [-1 .. +1]

          const seed = hashSeed(`${item?.instanceId ?? idx}:${driftStep}`);
          const jitterX = reducedMotion ? 0 : unitSigned(seed) * 3.5;
          const jitterY = reducedMotion ? 0 : unitSigned(seed >> 3) * 3.2;
          const jitterRot = reducedMotion ? 0 : unitSigned(seed >> 6) * 1.9;

          // Tighter overlap as hand grows on portrait phones
          const baseSpacing = count <= 5 ? 64 : count <= 8 ? 50 : Math.max(26, Math.floor(360 / count));
          let x = (idx - mid) * baseSpacing + jitterX;

          // Asymmetric arc: steeper drop on the far/right side (norm > 0)
          const steepFactor = norm > 0 ? 1.48 : 0.84;
          let y = Math.pow(Math.abs(norm), 1.85) * 18 * steepFactor + (norm > 0 ? norm * 5 : 0) + jitterY;
          let rot = norm * 9.5 + handLeanDeg + jitterRot;
          let scale = 1;
          let zIndex = 10 + idx;

          if (activeIdx !== null) {
            if (idx === activeIdx) {
              y = -32;
              rot = 0;
              scale = 1.16;
              zIndex = 60;
            } else {
              const dist = idx - activeIdx;
              const dir = Math.sign(dist);
              const push = Math.max(10, 32 - Math.abs(dist) * 7);
              x += dir * push;
            }
          }

          const isPlayable =
            Boolean(item?.card) &&
            isMyTurnMain &&
            (item!.card!.typeLine.toLowerCase().includes('land') || item!.card!.cmc <= 3);

          return (
            <div
              key={item?.instanceId ?? `sleeve_${idx}`}
              style={{
                transform: `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0) rotate(${rot.toFixed(2)}deg) scale(${scale})`,
                zIndex,
              }}
              onMouseEnter={() => {
                if (hoveredIdx !== idx) {
                  setHoveredIdx(idx);
                  soundEngine.play('hoverTick');
                }
              }}
              className={`absolute bottom-1 origin-bottom ${
                reducedMotion ? '' : 'transition-transform duration-150 ease-out'
              }`}
            >
              {item ? (
                <div className="relative">
                  <CardVisual
                    item={item}
                    sleeveUrl={sleeveUrl}
                    size="md"
                    playableGlow={isPlayable}
                    reducedMotion={reducedMotion}
                    onClick={(e) => {
                      e.stopPropagation();
                      // On touch / first tap: lift card and show quick action pill; second tap plays it!
                      if (selectedIdx === idx || e.detail >= 2) {
                        onPlayCard(item);
                        setSelectedIdx(null);
                      } else {
                        setSelectedIdx(idx);
                        soundEngine.play('hoverTick');
                      }
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault();
                      onOpenCardMenu(item, e);
                    }}
                    onInspect={() => onInspectCard(item)}
                  />
                  {activeIdx === idx && (
                    <div className="absolute -top-7 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-slate-950/95 border border-slate-700 rounded-full px-1.5 py-0.5 shadow-xl whitespace-nowrap z-50">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onPlayCard(item);
                        }}
                        className="px-1.5 py-0.5 rounded-full bg-amber-500 text-slate-950 font-bold text-[10px] hover:bg-amber-400"
                      >
                        Play
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onInspectCard(item);
                        }}
                        className="px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-200 text-[10px] hover:bg-slate-700"
                      >
                        Zoom
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenCardMenu(item, e);
                        }}
                        className="px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300 text-[10px] hover:bg-slate-700"
                      >
                        •••
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <CardSleeveBack sleeveUrl={sleeveUrl} size="md" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
